import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, MagnifyingGlass, ArrowCounterClockwise, Warning } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import {
  adminListUsers, adminCreateUser, adminUpdateUser, adminDeleteUser,
  adminRestoreUser, adminHardDeleteUser,
  adminListUserOrgs, adminAssignUserToOrg, adminRemoveUserFromOrg,
  adminListOrgs, formatAdminError,
} from '../../lib/admin';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import ArchiveToggle from '../../components/admin/ArchiveToggle';
import { Field, inputCls, selectCls, monoCls, Modal } from '../../components/admin/Field';
import { getNotificationLanguages } from '../../lib/api';
import { NOTIFY_LANG_FALLBACK, notifyLangLabel } from '../../lib/notifyLanguages';

const ROLES = ['admin', 'caregiver', 'family'];
// Org-admins (non-super) may only assign caregiver/family, never admin.
const ORG_ADMIN_ROLES = ['caregiver', 'family'];

export default function AdminUsers() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { current, isSuperAdmin } = useOrg();
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [hardConfirm, setHardConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [viewArchived, setViewArchived] = useState(false);
  const [langs, setLangs] = useState(NOTIFY_LANG_FALLBACK);

  const allowed = user?.role === 'admin';

  const reload = async () => {
    setLoading(true);
    try {
      const { items } = await adminListUsers({ archived: viewArchived });
      let list = viewArchived ? items.filter((x) => x.deleted_at) : items;
      // Per-org membership role is the source of truth. Prefer a backend-provided
      // `org_role`; otherwise fetch each member's role in the active org.
      if (!viewArchived && current?.id && list.length && list[0].org_role === undefined) {
        list = await Promise.all(list.map(async (u) => {
          try {
            const ms = await adminListUserOrgs(u.id);
            const m = ms.find((x) => (x.organization_id || x.id) === current.id);
            return { ...u, org_role: m?.role ?? null };
          } catch {
            return { ...u, org_role: null };
          }
        }));
      }
      setItems(list);
    } catch (err) {
      toast.error(formatAdminError('List users', err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!allowed) return;
    reload();
    getNotificationLanguages()
      .then((l) => { if (Array.isArray(l) && l.length) setLangs(l); })
      .catch(() => {});
    if (isSuperAdmin) {
      adminListOrgs().then(({ items }) => setOrgs(items)).catch(() => setOrgs([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, isSuperAdmin, viewArchived]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((u) => u.name?.toLowerCase().includes(needle) || u.email?.toLowerCase().includes(needle));
  }, [items, q]);

  const onSave = async (form, userOrgs) => {
    setBusy(true);
    try {
      let saved;
      if (editing === 'new') {
        saved = await adminCreateUser(form);
        toast.success(t('admin_user_created'));
      } else {
        // Don't send empty password on update.
        const body = { ...form };
        if (!body.password) delete body.password;
        saved = await adminUpdateUser(editing.id, body);
        toast.success(t('admin_user_updated'));
      }
      // Sync org memberships if super-admin edited them.
      if (isSuperAdmin && userOrgs) {
        const targetId = saved?.id || editing?.id;
        await syncMemberships(targetId, userOrgs).catch((e) => {
          toast.error(formatAdminError('Sync memberships', e));
        });
      }
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError(editing === 'new' ? 'Create user' : 'Update user', err));
    } finally {
      setBusy(false);
    }
  };

  const syncMemberships = async (userId, desired) => {
    // desired = [{org_id, role}, ...]
    const existing = await adminListUserOrgs(userId).catch(() => []);
    const existingByOrg = new Map(existing.map((m) => [m.organization_id || m.id, m]));
    const desiredByOrg = new Map(desired.map((d) => [d.org_id, d]));

    // Add new / update changed.
    for (const d of desired) {
      const cur = existingByOrg.get(d.org_id);
      if (!cur) {
        await adminAssignUserToOrg(d.org_id, { user_id: userId, role: d.role });
      } else if (cur.role !== d.role) {
        // Remove + re-add to change role. Simpler than introducing a PATCH endpoint for v1.
        await adminRemoveUserFromOrg(d.org_id, userId);
        await adminAssignUserToOrg(d.org_id, { user_id: userId, role: d.role });
      }
    }
    // Remove any membership that's no longer desired.
    for (const m of existing) {
      const orgId = m.organization_id || m.id;
      if (!desiredByOrg.has(orgId)) {
        await adminRemoveUserFromOrg(orgId, userId);
      }
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await adminDeleteUser(confirm.id);
      toast.success(t('admin_user_deleted'));
      setConfirm(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError('Delete user', err));
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (u) => {
    setBusy(true);
    try {
      await adminRestoreUser(u.id);
      toast.success(t('admin_restored'));
      reload();
    } catch (err) {
      toast.error(formatAdminError('Restore user', err));
    } finally {
      setBusy(false);
    }
  };

  const onHardDelete = async () => {
    setBusy(true);
    try {
      await adminHardDeleteUser(hardConfirm.id);
      toast.success(t('admin_hard_deleted'));
      setHardConfirm(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError('Permanently delete user', err));
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) {
    return (
      <AdminPage title={t('admin_users_title')}>
        <div className="bg-bg-alt rounded-xl p-8 text-center text-ink-soft">{t('admin_admin_only')}</div>
      </AdminPage>
    );
  }

  return (
    <AdminPage
      title={t('admin_users_title')}
      description={isSuperAdmin ? t('admin_users_sub_super') : t('admin_users_sub_org', { org: current?.name || '—' })}
    >

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            type="text"
            placeholder={t('search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-users-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-stone_line bg-white text-sm focus:outline-none focus:border-sage"
          />
        </div>
        {!viewArchived && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            data-testid="admin-user-new"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700"
          >
            <Plus size={16} weight="bold" /> {t('admin_user_new')}
          </button>
        )}
      </div>

      {isSuperAdmin && (
        <div className="flex items-center mb-3">
          <ArchiveToggle value={viewArchived} onChange={setViewArchived} />
        </div>
      )}

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink-muted">{t('loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">
            {viewArchived ? t('admin_no_archived') : t('no_results')}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-bg-alt text-ink-soft text-left text-xs uppercase tracking-[0.18em]">
              <tr>
                <th className="px-5 py-3 font-medium">{t('admin_field_name')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_email')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_role')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_language')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_phone')}</th>
                <th className="px-5 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone_line">
              {filtered.map((u) => (
                <tr key={u.id} className={`hover:bg-bg-alt/40 ${viewArchived ? 'opacity-70' : ''}`}>
                  <td className="px-5 py-3 font-medium text-ink">{u.name || '—'}</td>
                  <td className="px-5 py-3 text-ink-soft font-mono text-xs">{u.email}</td>
                  <td className="px-5 py-3">
                    <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-sage-100 text-sage-700">
                      {t(`role_${u.org_role || u.role}`) || (u.org_role || u.role)}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-ink-soft text-xs" data-testid={`admin-user-language-${u.id}`}>
                    {notifyLangLabel(u.language)}
                  </td>
                  <td className="px-5 py-3 text-ink-soft font-mono text-xs">{u.phone || '—'}</td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {viewArchived ? (
                      <>
                        <button onClick={() => onRestore(u)} disabled={busy} data-testid={`admin-user-restore-${u.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-sage-100 hover:text-sage-700 mr-1 disabled:opacity-50"
                          aria-label={t('admin_restore')} title={t('admin_restore')}>
                          <ArrowCounterClockwise size={16} />
                        </button>
                        <button onClick={() => setHardConfirm(u)} disabled={busy} data-testid={`admin-user-hard-delete-${u.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700 disabled:opacity-50"
                          aria-label={t('admin_hard_delete')} title={t('admin_hard_delete')}>
                          <Warning size={16} weight="bold" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditing(u)} data-testid={`admin-user-edit-${u.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-bg-alt hover:text-ink mr-1" aria-label={t('edit')}>
                          <PencilSimple size={16} />
                        </button>
                        <button onClick={() => setConfirm(u)} data-testid={`admin-user-delete-${u.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700" aria-label={t('admin_delete')}>
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <UserForm
          user={editing === 'new' ? null : editing}
          orgs={orgs}
          langs={langs}
          showOrgPicker={isSuperAdmin}
          activeOrg={current}
          onCancel={() => setEditing(null)}
          onSave={onSave}
          busy={busy}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={t('admin_user_delete_title')}
        description={confirm ? t('admin_user_delete_confirm', { name: confirm.name || confirm.email }) : ''}
        confirmLabel={t('admin_delete')}
        danger
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={onDelete}
      />

      <ConfirmDialog
        open={!!hardConfirm}
        title={t('admin_hard_delete_title')}
        description={hardConfirm ? t('admin_hard_delete_confirm', { name: hardConfirm.name || hardConfirm.email }) : ''}
        confirmLabel={t('admin_hard_delete')}
        danger
        busy={busy}
        onCancel={() => setHardConfirm(null)}
        onConfirm={onHardDelete}
      />
    </AdminPage>
  );
}

function UserForm({ user, orgs, langs = NOTIFY_LANG_FALLBACK, showOrgPicker, activeOrg, onCancel, onSave, busy }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'caregiver',
    phone: user?.phone || '',
    language: user?.language || 'en',
  });
  // Memberships state, only used for super-admin.
  const [memberships, setMemberships] = useState([]);
  const [loadingM, setLoadingM] = useState(false);

  // Pre-load existing memberships when editing.
  useEffect(() => {
    if (!showOrgPicker) return;
    if (user) {
      setLoadingM(true);
      adminListUserOrgs(user.id)
        .then((list) => setMemberships(
          list.map((m) => ({ org_id: m.organization_id || m.id, role: m.role || form.role }))
        ))
        .catch(() => setMemberships([]))
        .finally(() => setLoadingM(false));
    } else if (activeOrg) {
      // New user defaults to current active org.
      setMemberships([{ org_id: activeOrg.id, role: form.role }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, showOrgPicker, activeOrg]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    if (!user && !form.password) return; // Password required on create.
    let payload = form;
    if (showOrgPicker) {
      // Per-org membership roles are the source of truth; derive the legacy
      // global users.role from the active org's membership so it stays aligned.
      const activeM = memberships.find((m) => m.org_id === activeOrg?.id) || memberships[0];
      payload = { ...form, role: activeM?.role || 'caregiver' };
    }
    onSave(payload, showOrgPicker ? memberships : null);
  };

  // Org-admins may only assign caregiver/family; keep an existing higher role
  // as an option so editing doesn't silently downgrade it.
  const orgAdminRoleOptions = ORG_ADMIN_ROLES.includes(form.role)
    ? ORG_ADMIN_ROLES
    : [form.role, ...ORG_ADMIN_ROLES];

  const toggleOrg = (orgId, role = 'caregiver') => {
    setMemberships((prev) => {
      const existing = prev.find((m) => m.org_id === orgId);
      if (existing) return prev.filter((m) => m.org_id !== orgId);
      return [...prev, { org_id: orgId, role }];
    });
  };

  const setMemberRole = (orgId, role) => {
    setMemberships((prev) => prev.map((m) => (m.org_id === orgId ? { ...m, role } : m)));
  };

  return (
    <Modal onCancel={onCancel} wide>
      <form
        onSubmit={submit}
        data-testid="admin-user-form"
        className="space-y-4"
      >
        <h2 className="font-heading text-2xl text-ink">
          {user ? t('admin_user_edit_title') : t('admin_user_new_title')}
        </h2>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t('admin_field_name')} required>
            <input type="text" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="admin-user-name"
              className={inputCls} />
          </Field>
          <Field label={t('admin_field_email')} required>
            <input type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="admin-user-email"
              className={monoCls} />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t('admin_field_password')} required={!user}>
            <input type="password" value={form.password}
              required={!user}
              autoComplete="new-password"
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="admin-user-password"
              placeholder={user ? t('admin_password_keep') : ''}
              className={monoCls} />
          </Field>
          <Field label={t('admin_field_phone')}>
            <input type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="admin-user-phone"
              className={monoCls} />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {!showOrgPicker && (
            <Field label={t('admin_field_role')} required>
              <select value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="admin-user-role"
                className={selectCls}>
                {orgAdminRoleOptions.map((r) => <option key={r} value={r}>{t(`role_${r}`) || r}</option>)}
              </select>
            </Field>
          )}
          <Field label={t('admin_field_language')}>
            <select value={form.language}
              onChange={(e) => setForm({ ...form, language: e.target.value })}
              data-testid="admin-user-language"
              className={selectCls}>
              {langs.map((code) => <option key={code} value={code}>{notifyLangLabel(code)}</option>)}
            </select>
          </Field>
        </div>

        {showOrgPicker && (
          <div className="border-t border-stone_line pt-4">
            <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-semibold mb-2">
              {t('admin_user_memberships')}
            </div>
            {loadingM ? (
              <div className="text-sm text-ink-muted">{t('loading')}</div>
            ) : orgs.length === 0 ? (
              <div className="text-sm text-ink-muted">{t('admin_no_orgs_yet')}</div>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto" data-testid="admin-user-memberships">
                {orgs.map((o) => {
                  const m = memberships.find((x) => x.org_id === o.id);
                  return (
                    <li key={o.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-stone_line">
                      <input type="checkbox" checked={!!m}
                        onChange={() => toggleOrg(o.id, 'caregiver')}
                        data-testid={`admin-user-org-${o.id}`}
                        className="rounded text-sage focus:ring-sage" />
                      <div className="flex-1 text-sm font-medium text-ink">{o.name}</div>
                      {m && (
                        <select value={m.role}
                          onChange={(e) => setMemberRole(o.id, e.target.value)}
                          data-testid={`admin-user-org-role-${o.id}`}
                          className="text-xs px-2 py-1 rounded border border-stone_line bg-white">
                          {ROLES.map((r) => <option key={r} value={r}>{t(`role_${r}`) || r}</option>)}
                        </select>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-stone_line">
          <button type="button" onClick={onCancel} disabled={busy}
            data-testid="admin-user-cancel"
            className="px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-bg-alt disabled:opacity-50">
            {t('cancel')}
          </button>
          <button type="submit" disabled={busy || !form.name.trim() || !form.email.trim() || (!user && !form.password)}
            data-testid="admin-user-save"
            className="px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50">
            {busy ? '…' : t('save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Local Field helper removed — using the shared Field from
// components/admin/Field.js for visual consistency across admin forms.
